import React from 'react';

import type { ActivityViewProps } from '../../core/types/activity.types';

import type { PollMultipleChoiceSlide, PollSlide } from '../../core/types/payloads/poll.types';

import { LiveChoiceBars } from '../shared/LiveChoiceBars';

import {
  PollSlideAggregate,
  WordCloudInput,
  ScaleInput,
  SliderInput,
  formatPollAnswer,
  hasPollAnswer,
} from './PollSlideVisuals';

const OPTION_COLORS = ['#5b21b6', '#6d28d9', '#047857', '#10b981'];

function PollShell({ children, badge }: { children: React.ReactNode; badge?: string }) {
  return (
    <div className="plugin-shell relative overflow-hidden rounded-2xl p-6 sm:p-8">
      {badge && (
        <span className="plugin-shell__badge mb-4 inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-widest">
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}

function getMcCounts(slide: PollSlide, runtime: ActivityViewProps<'poll'>['runtime']) {
  if (slide.kind !== 'multiple-choice') return { counts: [] as number[], total: 0 };
  const agg = runtime.aggregates?.bySlide?.[slide.id];
  const counts = agg?.kind === 'multiple-choice' ? agg.counts : slide.options.map(() => 0);
  const total = counts.reduce((a, b) => a + b, 0);
  return { counts, total };
}

function PollPresentation({
  payload,
  runtime,
  slide,
  size = 'md',
  badge,
  highlightParticipantId,
  maskOtherNames = false,
}: {
  payload: ActivityViewProps<'poll'>['payload'];
  runtime: ActivityViewProps<'poll'>['runtime'];
  slide: PollSlide;
  size?: 'md' | 'lg';
  badge?: string;
  highlightParticipantId?: string;
  maskOtherNames?: boolean;
}) {
  const { counts, total } = getMcCounts(slide, runtime);
  const isWordCloud = slide.kind === 'word-cloud';
  const isResults = badge?.includes('Resultados');

  return (
    <PollShell badge={badge}>
      <h3 className={`plugin-host-title ${size === 'lg' ? 'plugin-host-title--lg' : ''}`}>{payload.title}</h3>

      <p className={`plugin-host-prompt mt-4 ${size === 'lg' ? 'plugin-host-prompt--lg' : ''}`}>{slide.prompt}</p>

      {slide.kind === 'multiple-choice' && (
        <div className="mt-8">
          <LiveChoiceBars
            size={size}
            total={total}
            items={slide.options.map((opt, i) => ({
              label: opt,
              count: counts[i] ?? 0,
              accent: OPTION_COLORS[i % OPTION_COLORS.length],
            }))}
          />
        </div>
      )}

      {slide.kind !== 'multiple-choice' && (
        <div className={isWordCloud && size === 'lg' ? 'mt-5 w-full' : 'mt-8'}>
          <PollSlideAggregate
            slide={slide}
            runtime={runtime}
            size={size}
            slideIndex={runtime.currentSlideIndex}
            highlightParticipantId={highlightParticipantId}
            maskOtherNames={maskOtherNames}
          />
        </div>
      )}

      {isResults && (
        <p className="mt-6 text-center font-display text-lg font-black uppercase tracking-wide text-[var(--sala-electric-purple)]">
          Resultados en vivo
        </p>
      )}
    </PollShell>
  );
}

function PollActiveInput({
  slide,
  dispatch,
}: {
  slide: PollSlide;
  dispatch: ActivityViewProps<'poll'>['dispatch'];
}) {
  const submit = (payload: unknown) => dispatch({ type: 'RESPOND', payload });

  if (slide.kind === 'multiple-choice') {
    const mc = slide as PollMultipleChoiceSlide;
    return (
      <div className="mt-6 grid gap-3">
        {mc.options.map((opt, i) => (
          <button key={`${opt}-${i}`} type="button" className="poll-option-tile" onClick={() => submit(i)}>
            <span className="relative flex items-center gap-3">
              <span
                className="poll-option-letter"
                style={{ background: OPTION_COLORS[i % OPTION_COLORS.length], color: '#fff' }}
              >
                {String.fromCharCode(65 + i)}
              </span>
              {opt}
            </span>
          </button>
        ))}
      </div>
    );
  }

  if (slide.kind === 'word-cloud') {
    return <WordCloudInput slide={slide} onSubmit={(words) => submit(words)} />;
  }

  if (slide.kind === 'scale') {
    return <ScaleInput slide={slide} onSubmit={(value) => submit(value)} />;
  }

  if (slide.kind === 'slider') {
    return <SliderInput slide={slide} onSubmit={(value) => submit(value)} />;
  }

  return null;
}

function studentBadge(slide: PollSlide): string {
  switch (slide.kind) {
    case 'word-cloud':
      return 'Lluvia de ideas';
    case 'scale':
      return 'Escala · Elige un valor';
    case 'slider':
      return 'Desliza · Ajusta y confirma';
    default:
      return 'Poll · Elige tu respuesta';
  }
}

export function PollHostView(props: ActivityViewProps<'poll'>) {
  const slide = props.payload.slides[props.runtime.currentSlideIndex];
  if (!slide) return null;

  const phaseLabel =
    props.phase === 'RESULTS'
      ? 'Resultados'
      : props.phase === 'ACTIVE'
        ? 'En juego · agregación en vivo'
        : 'Preparando';

  return (
    <PollPresentation
      payload={props.payload}
      runtime={props.runtime}
      slide={slide}
      badge={`${phaseLabel} · ${props.runtime.currentSlideIndex + 1}/${props.payload.slides.length}`}
    />
  );
}

export function PollStudentView(props: ActivityViewProps<'poll'>) {
  const { payload, runtime, participantId, phase, dispatch, examAutonomous } = props;
  const slide = payload.slides[runtime.currentSlideIndex];

  if (!slide) {
    return (
      <PollShell>
        <p className="font-bold text-slate-600">Esperando pregunta…</p>
      </PollShell>
    );
  }

  const myAnswer = participantId ? runtime.responses[participantId]?.[slide.id] : undefined;
  const hasAnswered = hasPollAnswer(slide, myAnswer);
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';
  const showResults = phase === 'RESULTS' || phase === 'PODIUM';
  const showLive = payload.settings?.showLiveResults !== false && showResults;

  if (waiting) {
    return (
      <PollShell badge={studentBadge(slide)}>
        <p className="font-display text-xl font-black leading-snug sm:text-2xl">{slide.prompt}</p>
        <p className="plugin-wait-banner">
          Espera a que el profesor pulse <strong className="text-[var(--sala-electric-purple)]">Comenzar</strong>
        </p>
      </PollShell>
    );
  }

  if (showResults) {
    const { counts, total } = getMcCounts(slide, runtime);
    return (
      <PollShell badge="Tus resultados">
        <p className="font-display text-xl font-black leading-snug sm:text-2xl">{slide.prompt}</p>
        {hasAnswered && (
          <div className="plugin-answer-card">
            <p className="text-xs font-black uppercase tracking-widest text-slate-600">Tu respuesta</p>
            <p className="mt-1 font-display text-lg font-black">{formatPollAnswer(slide, myAnswer)}</p>
          </div>
        )}
        {!hasAnswered && <p className="plugin-muted-note mt-4">No enviaste respuesta en esta ronda</p>}
        {showLive && slide.kind === 'multiple-choice' && (
          <div className="mt-6">
            <LiveChoiceBars
              total={total}
              items={(slide as PollMultipleChoiceSlide).options.map((opt, i) => ({
                label: opt,
                count: counts[i] ?? 0,
                accent: OPTION_COLORS[i % OPTION_COLORS.length],
                isSelected: myAnswer === i,
              }))}
            />
          </div>
        )}
        {showLive && slide.kind !== 'multiple-choice' && (
          <div className="mt-6">
            <PollSlideAggregate
              slide={slide}
              runtime={runtime}
              highlightValue={myAnswer}
              slideIndex={runtime.currentSlideIndex}
              highlightParticipantId={participantId}
              maskOtherNames={payload.settings?.anonymous !== false}
            />
          </div>
        )}
      </PollShell>
    );
  }

  if (hasAnswered && examAutonomous) {
    return (
      <PollShell badge="Respuesta registrada">
        <p className="font-display text-xl font-black leading-snug sm:text-2xl">{slide.prompt}</p>
        <div className="plugin-answer-card--sent">
          <p className="text-xs font-black uppercase tracking-widest text-slate-600">Has enviado</p>
          <p className="mt-2 font-display text-2xl font-black">{formatPollAnswer(slide, myAnswer)}</p>
        </div>
        <p className="plugin-muted-note mt-4">Pulsa «Continuar al siguiente bloque» cuando estés listo.</p>
      </PollShell>
    );
  }

  if (hasAnswered) {
    return (
      <PollShell badge="Respuesta enviada">
        <p className="font-display text-xl font-black leading-snug sm:text-2xl">{slide.prompt}</p>
        <div className="plugin-answer-card--sent">
          <p className="text-xs font-black uppercase tracking-widest text-slate-600">Has enviado</p>
          <p className="mt-2 font-display text-2xl font-black">{formatPollAnswer(slide, myAnswer)}</p>
        </div>
        <p className="plugin-muted-note mt-4">Esperando al resto de la clase…</p>
      </PollShell>
    );
  }

  return (
    <PollShell badge={studentBadge(slide)}>
      <p className="font-display text-xl font-black leading-snug sm:text-2xl">{slide.prompt}</p>
      <PollActiveInput slide={slide} dispatch={dispatch} />
    </PollShell>
  );
}

export function PollProjectorView(props: ActivityViewProps<'poll'>) {
  const slide = props.payload.slides[props.runtime.currentSlideIndex];
  if (!slide) return null;

  return (
    <PollPresentation
      payload={props.payload}
      runtime={props.runtime}
      slide={slide}
      size="lg"
      badge={
        props.phase === 'RESULTS'
          ? `Resultados · ${props.runtime.currentSlideIndex + 1}/${props.payload.slides.length}`
          : `En vivo · ${props.runtime.currentSlideIndex + 1}/${props.payload.slides.length}`
      }
    />
  );
}
